import type { PreviewTabSignal } from "@/lib/use-widget-env";
import type { WidgetFeatures } from "@/lib/widget-config";
import { pickInitialTab, type WidgetRoute } from "@/lib/widget-route";
import { defaultRouteFor } from "./helpers";

/**
 * The route a host-driven preview signal (`env.previewTab`, set only by the dev playground) asks for. A request
 * for a disabled tab falls back to the first enabled one; `messages` resumes into the latest thread; an `article`
 * opens that demo help article / news post with Back returning to its tab. An article whose feature is off falls
 * back to the plain tab, like any other disabled surface.
 */
export function previewRoute(signal: PreviewTabSignal, features: WidgetFeatures): WidgetRoute {
  const { article } = signal;
  if (article?.kind === "help" && features.help) {
    return { kind: "help-article", slug: article.slug, from: "search", origin: { kind: "help" } };
  }
  if (article?.kind === "news" && features.news) {
    return { kind: "news-article", id: article.id, origin: { kind: "news" } };
  }
  const tab = pickInitialTab(signal.tab, features);
  return tab === "messages"
    ? { kind: "messages-thread", pendingLatest: true }
    : defaultRouteFor(tab);
}

/** Identity of a navigation request by VALUE: the same `nonce` (and target) is the same request, whatever the
 *  object reference, so a host re-rendering with a fresh-but-equal object does not re-navigate. */
export function previewTabSignalKey(signal: PreviewTabSignal): string {
  const { article } = signal;
  return [
    signal.tab,
    signal.nonce,
    article?.kind ?? "",
    article ? (article.kind === "help" ? article.slug : article.id) : "",
  ].join("|");
}
